import React, { Fragment, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import clsx from 'clsx';

import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import { useUser } from '@hooks/useUser';
import { useStyles } from './style';

export const CollapsedMenu = ({
  filteredTopItems,
  filteredBottomItems,
  offset,
  menuOpen,
  currentMenuIndex,
  setCurrentMenu,
}) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const { userHasScope } = useUser();
  const classes = useStyles({ offset, menuOpen });
  const [hoveredItem, setHoveredItem] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const redirectToRoute = (route, slug) => {
    setCurrentMenu(slug);
    return history.push(`/${route}`);
  };

  const handleMouseEnter = (event, item) => {
    if (item.children && item.children.length > 0) {
      setHoveredItem(item.id);
      setAnchorEl(event.currentTarget);
    }
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
    setAnchorEl(null);
  };

  const isRouteSelected = (children, parentIndex) => {
    if (!parentIndex) {
      return (
        children?.filter((child) => pathname.includes(child.slug))?.length > 0
      );
    }
    return pathname.split('/').indexOf(children.slug) > -1;
  };

  const getItem = (item) => {
    const hasChildren = item.children && item.children.length > 0;
    const isHovered = hoveredItem === item.id;

    if (hasChildren) {
      const containsPermissions = item.children.some(
        (child) => child.permissions
      );
      const hasSomePermission = item.children.some((child) =>
        userHasScope(child.permissions)
      );

      if (!containsPermissions || hasSomePermission) {
        return (
          <Fragment key={item.id}>
            <Box
              className={clsx(classes.menuItemWrapper, {
                [classes.menuItemSelected]: currentMenuIndex === item.slug,
              })}
              onMouseEnter={(e) => handleMouseEnter(e, item)}
              onMouseLeave={handleMouseLeave}
            >
              <Box
                className={clsx(
                  classes.menuItemIconOnly,
                  classes.menuItemWithChildren,
                  isHovered && classes.menuItemHovered
                )}
              >
                {item.icon}
              </Box>

              {hoveredItem === item.id && (
                <Popover
                  open={true}
                  anchorEl={anchorEl}
                  onClose={handleMouseLeave}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  disableRestoreFocus
                  sx={{
                    pointerEvents: 'none',
                    '& .MuiPopover-paper': {
                      pointerEvents: 'auto',
                    },
                  }}
                >
                  <List>
                    {item.children.map((child) => (
                      <ListItem
                        key={child.id}
                        className={clsx(classes.nestedMenuItem, {
                          [classes.menuItemSelected]: isRouteSelected(
                            child,
                            item.slug
                          ),
                        })}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          if (child.onClick) {
                            return child.onClick(child.route);
                          }

                          return redirectToRoute(child.slug, child.slug);
                        }}
                      >
                        {child.icon && (
                          <ListItemIcon className={classes.nestedMenuIcon}>
                            {child.icon}
                          </ListItemIcon>
                        )}
                        <ListItemText
                          primary={child.label}
                          className={classes.nestedMenuText}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Popover>
              )}
            </Box>
          </Fragment>
        );
      }

      return;
    }

    return (
      (!item.permissions || userHasScope(item.permissions)) && (
        <Fragment key={item.id}>
          <Box
            className={clsx(classes.menuItemWrapper, {
              [classes.menuItemSelected]: currentMenuIndex === item.slug,
            })}
            onMouseEnter={(e) => handleMouseEnter(e, item)}
            onMouseLeave={handleMouseLeave}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              if (item.onClick) {
                return item.onClick();
              }

              return redirectToRoute(item.slug, item.slug);
            }}
          >
            <Box
              className={clsx(
                classes.menuItemIconOnly,
                isHovered && classes.menuItemHovered
              )}
            >
              {item.icon}
            </Box>
          </Box>
        </Fragment>
      )
    );
  };

  return (
    <Box className={classes.menuCollapsedContainer}>
      <Box className={classes.topMenuSection}>
        {filteredTopItems.map((item) => getItem(item))}
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      <Box className={classes.bottomMenuSection}>
        <Divider className={classes.sectionSeparator} />
        {filteredBottomItems.map((item) => getItem(item))}
      </Box>
    </Box>
  );
};

export default CollapsedMenu;
